import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, BadgeCheck, Ban, CalendarClock, CheckCircle2, ChevronDown, Clock,
  Loader2, MapPin, Phone, RefreshCcw, Store, Wallet, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { aggregatorApi, formatApiError, waLink } from "../lib/api";
import { AGG_ACTIVE_STATUSES, AGG_STATUSES, CANCELLATION_POLICY, CANCELLATION_REASONS } from "../config/defaults";
import { useApp } from "../context/AppContext";
import { prettyDate, prettySlot, directionsUrl } from "../components/AggregatorBookingModal";

const STATUS_TONE = {
  "Booking Requested": "bg-amber-400/10 text-amber-300 border-amber-400/30",
  "Booking Confirmed": "bg-carza-dim text-carza-lite border-carza/30",
  "Reschedule Requested": "bg-carza-dim text-carza-lite border-carza/30",
  "Service In Progress": "bg-carza-dim text-carza-lite border-carza/30",
  "Service Completed": "bg-emerald-400/10 text-emerald-300 border-emerald-400/30",
  "Cancelled by Customer": "bg-white/5 text-mist-2 border-white/10",
  "Cancelled by Centre": "bg-red-400/10 text-red-300 border-red-400/30",
  "No-Show": "bg-red-400/10 text-red-300 border-red-400/30",
};

export default function BookingDetailsPage() {
  const { ref } = useParams();
  const { config } = useApp();
  const [phone, setPhone] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("carza_agg_booking") || "null")?.phone || ""; } catch { return ""; }
  });
  const [phoneInput, setPhoneInput] = useState("");
  const [booking, setBooking] = useState(null);
  const [window_, setWindow] = useState(null);
  const [loading, setLoading] = useState(!!phone);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("view"); // view | cancel | reschedule | cancelled

  const load = async (p) => {
    setLoading(true); setError("");
    try {
      const res = await aggregatorApi.booking(ref, p);
      setBooking(res.booking);
      setWindow(res.cancellation);
    } catch (e) {
      setError(formatApiError(e, "Could not load this booking."));
      setBooking(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (phone) load(phone); }, []); // eslint-disable-line

  const submitPhone = (e) => {
    e.preventDefault();
    const digits = phoneInput.replace(/\D/g, "").slice(-10);
    if (digits.length !== 10) return setError("Enter the 10-digit mobile number used for the booking");
    setPhone(digits);
    sessionStorage.setItem("carza_agg_booking", JSON.stringify({ ref, phone: digits }));
    load(digits);
  };

  return (
    <main className="flex min-h-screen flex-col bg-ink px-5 pb-24 pt-[88px]" data-testid="booking-details-page">
      <div className="container-x w-full max-w-2xl">
        <Link to="/" className="btn btn-ghost mb-6 px-0 py-2 text-[13px]"><ArrowLeft size={15} /> Back to Home</Link>

        {!booking && !loading && (
          <div className="glass mx-auto flex max-w-sm flex-col gap-4 rounded-3xl p-7" data-testid="booking-phone-gate">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-carza-dim text-carza-lite"><Store size={20} /></span>
            <h1 className="font-display text-2xl font-bold text-paper">View your booking</h1>
            <p className="text-sm text-mist">Enter the mobile number you used while booking the slot ({ref}).</p>
            <form onSubmit={submitPhone} className="flex flex-col gap-3">
              <input className="field" value={phoneInput} onChange={(e) => setPhoneInput(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit mobile number" inputMode="tel" data-testid="booking-gate-phone-input" />
              {error && <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300" data-testid="booking-gate-error">{error}</p>}
              <button type="submit" className="btn btn-primary w-full py-3.5" data-testid="booking-gate-submit">View Booking</button>
            </form>
          </div>
        )}

        {loading && <div className="flex h-48 items-center justify-center"><Loader2 size={26} className="animate-spin text-carza-lite" /></div>}

        {booking && (
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {mode === "cancelled" ? (
              <CancelledDone booking={booking} onView={() => { setMode("view"); load(phone); }} />
            ) : mode === "cancel" ? (
              <CancelFlow booking={booking} phone={phone} window_={window_}
                onDone={() => { setMode("cancelled"); load(phone); }}
                onCancel={() => setMode("view")} />
            ) : mode === "reschedule" ? (
              <RescheduleFlow booking={booking} phone={phone}
                onDone={() => { setMode("view"); load(phone); toast.success("Booking rescheduled"); }}
                onCancel={() => setMode("view")} />
            ) : (
              <BookingView booking={booking} window_={window_} config={config} phone={phone}
                onCancel={() => setMode("cancel")} onReschedule={() => setMode("reschedule")} />
            )}
          </motion.div>
        )}
      </div>
    </main>
  );
}

function BookingView({ booking: b, window_, config, onCancel, onReschedule }) {
  const active = AGG_ACTIVE_STATUSES.includes(b.status);
  const statusIdx = AGG_STATUSES.indexOf(b.status);
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-paper" data-testid="booking-details-title">Booking {b.booking_ref}</h1>
          <p className="mt-1 text-sm text-mist">Carza Nearby booking · pay the centre directly</p>
        </div>
        <span className={`rounded-full border px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-widest ${STATUS_TONE[b.status] || ""}`} data-testid="booking-details-status">{b.status}</span>
      </div>

      {active && statusIdx >= 0 && (
        <div className="flex items-center gap-1.5" data-testid="booking-status-timeline">
          {AGG_STATUSES.slice(0, 5).map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1 rounded-full transition-colors duration-500 ${i <= statusIdx ? "bg-carza" : "bg-white/10"}`} />
              <p className={`mt-1.5 font-mono text-[8px] uppercase tracking-widest ${i === statusIdx ? "text-carza-lite" : "text-mist-2"}`}>{s.replace(" by Customer", "").replace(" by Centre", "")}</p>
            </div>
          ))}
        </div>
      )}

      <div className="glass flex flex-col gap-3 rounded-3xl p-6" data-testid="booking-details-card">
        {[
          ["Centre", `${b.centre_name} · ${b.centre_area}`, Store],
          ["Service", b.service, BadgeCheck],
          ["Vehicle", `${b.vehicle_category}${b.vehicle_number ? ` · ${b.vehicle_number}` : ""}`, CheckCircle2],
          ["Date & time", `${prettyDate(b.date)}, ${prettySlot(b.time_slot)}`, CalendarClock],
          ["Customer", `${b.customer_name} · +91 ${b.customer_phone}`, Phone],
        ].map(([k, v, Icon]) => (
          <div key={k} className="flex items-start gap-3 text-sm">
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-carza-dim text-carza-lite"><Icon size={13} /></span>
            <span><span className="block font-mono text-[9px] uppercase tracking-[0.2em] text-mist-2">{k}</span>
              <span className="mt-0.5 block font-medium text-paper">{v}</span></span>
          </div>
        ))}
        <div className="mt-1 flex items-start gap-3 rounded-2xl border border-carza/25 bg-carza/[0.07] p-4 text-sm" data-testid="booking-payment-note">
          <Wallet size={16} className="mt-0.5 shrink-0 text-carza-lite" />
          <span className="text-[13px] leading-relaxed text-paper/90">
            <span className="font-semibold text-carza-lite">Payment:</span> pay the washing centre directly after the service is completed. CARZA does not collect payment for CARZA Nearby bookings.
          </span>
        </div>
        {b.notes && <p className="text-[12px] text-mist">Notes: {b.notes}</p>}
      </div>

      <details className="rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3" data-testid="booking-policy">
        <summary className="flex cursor-pointer items-center justify-between text-[13px] font-semibold text-paper">
          Cancellation Policy <ChevronDown size={14} className="text-mist-2" />
        </summary>
        <ul className="mt-3 flex flex-col gap-2">
          {CANCELLATION_POLICY.map((p) => (
            <li key={p} className="flex items-start gap-2 text-[12px] leading-relaxed text-mist">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-carza" /> {p}
            </li>
          ))}
        </ul>
      </details>

      {window_?.allowed && active && b.status !== "Service In Progress" && (
        <div className="flex flex-col gap-2.5 sm:flex-row">
          <button onClick={onCancel} className="btn border border-red-400/40 px-6 py-3.5 text-[13px] text-red-300 hover:bg-red-400/10" data-testid="booking-cancel-btn">
            <Ban size={15} /> Cancel Booking
          </button>
          <button onClick={onReschedule} className="btn btn-outline px-6 py-3.5 text-[13px]" data-testid="booking-reschedule-btn">
            <RefreshCcw size={15} /> Reschedule
          </button>
        </div>
      )}
      {b.status === "Service In Progress" && (
        <p className="rounded-2xl border border-amber-400/25 bg-amber-400/[0.07] px-4 py-3 text-[13px] leading-relaxed text-amber-200" data-testid="booking-support-note">
          Service is in progress — cancellation is closed. Need help? <a className="font-semibold underline" href={waLink(config.brand.whatsapp, `Hi CARZA, I need help with booking ${b.booking_ref}.`)} target="_blank" rel="noopener noreferrer">Contact CARZA support</a>.
        </p>
      )}
      <a href={directionsUrl({ lat: null, lng: null, name: b.centre_name, area: b.centre_area })} target="_blank" rel="noopener noreferrer" className="btn btn-outline-dark w-full py-3.5 text-[13px]" data-testid="booking-directions-btn">
        <MapPin size={15} /> Get Directions
      </a>
    </div>
  );
}

function CancelFlow({ booking: b, phone, window_, onDone, onCancel }) {
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setLoading(true); setError("");
    try {
      await aggregatorApi.cancel(b.booking_ref, { phone, reason });
      toast.success("Booking cancelled");
      onDone();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <h1 className="font-display text-3xl font-bold text-paper">Cancel booking {b.booking_ref}</h1>
      <div className="glass rounded-3xl p-6 text-sm" data-testid="cancel-summary">
        <div className="flex justify-between py-1"><span className="text-mist">Centre</span><span className="text-paper">{b.centre_name}</span></div>
        <div className="flex justify-between py-1"><span className="text-mist">Service</span><span className="text-paper">{b.service}</span></div>
        <div className="flex justify-between py-1"><span className="text-mist">Date & time</span><span className="text-paper">{prettyDate(b.date)}, {prettySlot(b.time_slot)}</span></div>
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Why are you cancelling?</p>
        <div className="mt-3 flex flex-col gap-2">
          {CANCELLATION_REASONS.map((r) => (
            <button key={r} onClick={() => { setReason(r); setError(""); }}
              className={`rounded-2xl border px-4 py-3 text-left text-[14px] transition-all ${reason === r ? "border-carza/60 bg-carza-dim text-carza-lite" : "border-white/10 text-mist hover:border-white/25"}`}
              data-testid={`cancel-reason-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              {r}
            </button>
          ))}
        </div>
      </div>
      {reason && (
        <div className="rounded-2xl border border-amber-400/25 bg-amber-400/[0.07] px-4 py-3 text-[13px] leading-relaxed text-amber-200" data-testid="cancel-rule-note">
          {window_?.late
            ? "You are cancelling within 2 hours of your slot (or after the slot time). This will be recorded as a late cancellation. No fees apply."
            : window_?.rule}
          {confirming && <span className="mt-2 block font-semibold">Confirm: your slot will be released immediately.</span>}
        </div>
      )}
      {error && <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300" data-testid="cancel-error">{error}</p>}
      <div className="flex flex-col gap-2.5 sm:flex-row">
        <button onClick={onCancel} className="btn btn-ghost px-6 py-3.5 text-[13px]">Keep Booking</button>
        <button
          onClick={() => (confirming ? submit() : setConfirming(true))}
          disabled={!reason || loading}
          className="btn border border-red-400/40 px-6 py-3.5 text-[13px] text-red-300 hover:bg-red-400/10 disabled:opacity-40"
          data-testid="cancel-confirm-btn">
          {loading ? "Cancelling…" : confirming ? <>Confirm Cancellation <XCircle size={15} /></> : "Continue"}
        </button>
      </div>
    </div>
  );
}

function RescheduleFlow({ booking: b, phone, onDone, onCancel }) {
  const [dates, setDates] = useState([]);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [slotsNote, setSlotsNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    aggregatorApi.availability(b.centre_code).then((r) => setDates(r.dates || [])).catch(() => setDates([]));
  }, [b.centre_code]);

  const pickDate = async (d) => {
    setDate(d); setSlot(""); setError(""); setSlotsNote("");
    try {
      const res = await aggregatorApi.slots(b.centre_code, d);
      setSlots(res.slots || []);
      if (!res.availability_configured) setSlotsNote(res.note || "Availability to be confirmed");
    } catch (e) {
      setSlots([]); setSlotsNote(formatApiError(e));
    }
  };

  const submit = async () => {
    setLoading(true); setError("");
    try {
      await aggregatorApi.reschedule(b.booking_ref, { phone, new_date: date, new_slot: slot });
      onDone();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <h1 className="font-display text-3xl font-bold text-paper">Reschedule booking {b.booking_ref}</h1>
      <p className="text-sm text-mist">Current slot: {prettyDate(b.date)}, {prettySlot(b.time_slot)} at {b.centre_name}. The original slot is released once rescheduling succeeds.</p>
      <div>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">New date</span>
        <div className="no-scrollbar mt-2 flex gap-2 overflow-x-auto pb-1">
          {dates.map((d) => (
            <button key={d} onClick={() => pickDate(d)}
              className={`shrink-0 rounded-xl border px-4 py-2.5 text-[13px] font-medium transition-all ${date === d ? "border-carza/60 bg-carza-dim text-carza-lite" : "border-white/10 text-mist hover:border-white/25"}`}
              data-testid={`reschedule-date-${d}`}>{prettyDate(d)}</button>
          ))}
        </div>
      </div>
      {slotsNote && <p className="rounded-xl border border-amber-400/25 bg-amber-400/[0.07] px-4 py-3 text-[13px] text-amber-200">{slotsNote}</p>}
      {date && !slotsNote && (
        <div className="grid grid-cols-3 gap-2">
          {slots.map((s) => (
            <button key={s.slot} onClick={() => s.available && setSlot(s.slot)} disabled={!s.available}
              className={`rounded-xl border px-2 py-2.5 text-center text-[13px] font-medium transition-all ${slot === s.slot ? "border-carza/60 bg-carza-dim text-carza-lite" : s.available ? "border-white/10 text-mist hover:border-white/25" : "cursor-not-allowed border-white/5 text-mist-2/40"}`}
              data-testid={`reschedule-slot-${s.slot.replace(":", "-")}`}>
              {prettySlot(s.slot)}
              <span className="mt-0.5 block text-[9px] uppercase tracking-wider text-mist-2">{s.available ? `${s.remaining} left` : "Full"}</span>
            </button>
          ))}
        </div>
      )}
      {error && <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300" data-testid="reschedule-error">{error}</p>}
      <div className="flex flex-col gap-2.5 sm:flex-row">
        <button onClick={onCancel} className="btn btn-ghost px-6 py-3.5 text-[13px]">Back</button>
        <button onClick={submit} disabled={!date || !slot || loading} className="btn btn-primary px-6 py-3.5 text-[13px] disabled:opacity-40" data-testid="reschedule-confirm-btn">
          {loading ? "Rescheduling…" : "Confirm New Slot"}
        </button>
      </div>
    </div>
  );
}

function CancelledDone({ booking: b, onView }) {
  return (
    <div className="glass mx-auto flex max-w-md flex-col items-center gap-5 rounded-3xl p-8 text-center" data-testid="cancel-confirmation">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-red-400/10 text-red-300"><XCircle size={30} /></span>
      <div>
        <h2 className="font-display text-2xl font-bold text-paper">Booking cancelled</h2>
        <p className="mt-2 text-sm leading-relaxed text-mist">
          Booking {b.booking_ref} at {b.centre_name} has been cancelled and the slot has been released.
          You can book a new slot anytime.
        </p>
      </div>
      <details className="w-full rounded-2xl border border-white/10 px-4 py-3 text-left" data-testid="cancel-confirmation-policy">
        <summary className="flex cursor-pointer items-center justify-between text-[13px] font-semibold text-paper">
          Cancellation Policy <ChevronDown size={14} className="text-mist-2" />
        </summary>
        <ul className="mt-3 flex flex-col gap-2">
          {CANCELLATION_POLICY.map((p) => (
            <li key={p} className="flex items-start gap-2 text-[12px] leading-relaxed text-mist">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-carza" /> {p}
            </li>
          ))}
        </ul>
      </details>
      <button onClick={onView} className="btn btn-outline w-full py-3.5 text-[13px]">View Booking Status</button>
    </div>
  );
}
